/**
 * `createApiOrgUsersSource` — the permissions-mgmt-api org-user port over the
 * shared `ApiClient`.
 *
 * Backs the Team page's "Organization members" list + the "Invite people"
 * dialog: listing the org's members, the org role catalog for the role picker,
 * and inviting a person into the organization. The wire is snake_case
 * (`user_id`, `tenant_id`, `role_name`); this adapter maps it to the camelCase
 * summaries the UI renders. Canonical paths (`/org-users[/invite]`,
 * `/org-roles`) are rewritten onto the `.management.` permissions zone by the
 * dashboard-local rules in `app/api-client.ts`, so region routing / auth /
 * envelope unwrap are inherited.
 *
 * RBAC: list → `users:list`, roles → `roles:list`, invite → `users:create`
 * (the customer-facing scopes; internal staff management uses `internal-*`).
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import { asOptString, asString, rowsFromWire } from "./wire";

export interface OrgMember {
  id: string;
  email: string;
  name?: string;
  /** Org role names the member holds (permissions returns names, not ids). */
  roles: string[];
  /**
   * What a person reads for each name in `roles`, as permissions labels it: a
   * built-in without the deployment's prefix (`sandbox-Writer` → `Writer`; prd
   * names have none). Read through `orgRoleLabel`; the names stay the tokens.
   */
  roleLabels?: Record<string, string>;
  /** `active` | `pending_invitation` | `suspended` | `inactive`. */
  status: string;
  /**
   * The invitation is still outstanding — an email-keyed invite that has never
   * been accepted (server-side: `status === "pending_invitation"`). Drives the
   * "Pending" badge and the "Cancel invitation" + "Resend" (vs. "Remove") row
   * actions.
   */
  pending: boolean;
}

export interface OrgRole {
  /** Token to send in an invite: a default role's id IS its name; custom = `rol_…`. */
  id: string;
  name: string;
  /** The display name permissions gives it (see `OrgMember.roleLabels`). */
  label?: string;
  description?: string;
}

export interface InviteOrgUserInput {
  email: string;
  /** Org role tokens (default-role names or custom role ids). */
  roles: string[];
  /** Root/home tenant for the invitee. */
  tenantId: string;
}

export interface OrgUsersSource {
  /** Members of the caller's organization (permissions `users:list`). */
  listMembers(): Promise<OrgMember[]>;
  /** Org role catalog for the invite role picker (permissions `roles:list`). */
  listRoles(): Promise<OrgRole[]>;
  /** Invite a person into the organization (permissions `users:create`). */
  /**
   * Resolves with `emailSent` when the API reports it: `false` means the
   * invitation exists but its email did not go out (the send is best-effort).
   */
  invite(input: InviteOrgUserInput): Promise<{ emailSent?: boolean }>;
  /**
   * Change a member's org roles by applying an explicit delta (permissions
   * `users:edit`): `add` role tokens are granted, `remove` tokens revoked. A
   * token is a default role's name (mapped to its id server-side) or a custom
   * role id. Empty/omitted lists are skipped. `add` runs before `remove`; the
   * two sets are disjoint so order is immaterial. Throws a typed `ApiError` on
   * failure (e.g. 404 not a member, 403 missing scope).
   */
  setRoles(userId: string, changes: { add?: string[]; remove?: string[] }): Promise<void>;
  /**
   * Remove a member from the caller's org, or cancel their pending invitation
   * (permissions `users:delete`). The backend decides which from the membership
   * status: a `pending_invitation` (never accepted) is hard-canceled (its
   * membership + role grants deleted), an accepted member is soft-removed
   * (deactivated). Throws a typed `ApiError` on failure (e.g. 404 not a member,
   * 400 when you try to remove yourself).
   */
  remove(userId: string): Promise<void>;
  /**
   * Re-send the invitation email to a still-pending invitee (permissions
   * `users:create`). Rotates the invite link and re-delivers it. Resolves with
   * `emailSent` when the API reports it: `false` means the send was refused
   * AFTER the rotation, so the earlier link no longer works either. Throws a
   * typed `ApiError` on failure (e.g. 400 when the invite was already accepted,
   * 404 not a member).
   */
  resend(userId: string): Promise<{ emailSent?: boolean }>;
}

function labelsFromWire(raw: unknown): Record<string, string> {
  const labels: Record<string, string> = {};
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    for (const [name, label] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof label === "string" && label) labels[name] = label;
    }
  }
  return labels;
}

function memberFromWire(raw: Record<string, unknown>): OrgMember {
  const roles = Array.isArray(raw.roles) ? raw.roles.map((r: unknown) => String(r)) : [];
  return {
    id: asString(raw.user_id ?? raw.id),
    email: asString(raw.email),
    name: asOptString(raw.name),
    roles,
    roleLabels: labelsFromWire(raw.role_labels),
    status: asString(raw.status),
    // permissions sets `pending` from the membership status; fall back to the
    // raw status so the flag is robust even if the field is ever absent.
    pending: raw.pending === true || asString(raw.status) === "pending_invitation",
  };
}

/** The members in a `GET /org-users` payload (already envelope-unwrapped). */
export function orgMembersFromWire(data: unknown): OrgMember[] {
  return rowsFromWire(data)
    .map(memberFromWire)
    .filter((m) => m.email || m.id);
}

function roleFromWire(raw: Record<string, unknown>): OrgRole {
  const id = asString(raw.id ?? raw.role_name ?? raw.name);
  return {
    id,
    name: asString(raw.role_name ?? raw.name, id),
    label: asOptString(raw.label),
    description: asOptString(raw.description),
  };
}

export function createApiOrgUsersSource(client: ApiClient): OrgUsersSource {
  return {
    async listMembers(): Promise<OrgMember[]> {
      const res = await client.get<unknown>("/org-users");
      return orgMembersFromWire(unwrapData(res));
    },

    async listRoles(): Promise<OrgRole[]> {
      const res = await client.get<unknown>("/org-roles");
      return rowsFromWire(unwrapData(res))
        .map(roleFromWire)
        .filter((r) => r.id);
    },

    async invite(input: InviteOrgUserInput): Promise<{ emailSent?: boolean }> {
      // unwrapData throws a typed ApiError on an `ok:false` envelope (e.g. a
      // 409 when the person is already an active member) so the dialog can react.
      const result = unwrapData(
        await client.post<unknown>("/org-users/invite", {
          email: input.email,
          roles: input.roles,
          tenant_id: input.tenantId,
        }),
      ) as { email_sent?: unknown } | null;
      client.invalidate("/org-users");
      return typeof result?.email_sent === "boolean" ? { emailSent: result.email_sent } : {};
    },

    async setRoles(
      userId: string,
      changes: { add?: string[]; remove?: string[] },
    ): Promise<void> {
      // Both the assign (POST) and revoke (DELETE) endpoints share the
      // `/v0/users/{id}/roles` route (org taken from the token) reached via the
      // `/org-users` rewrite. unwrapData throws a typed ApiError on `ok:false`.
      const id = encodeURIComponent(userId);
      const add = changes.add ?? [];
      const remove = changes.remove ?? [];
      if (add.length > 0) {
        unwrapData(
          await client.post<unknown>(`/org-users/${id}/roles`, { roles: add }),
        );
      }
      if (remove.length > 0) {
        // DELETE carries a body via ApiRequestOptions (the endpoint reads it).
        unwrapData(
          await client.delete<unknown>(`/org-users/${id}/roles`, {
            body: { roles: remove },
          }),
        );
      }
      client.invalidate("/org-users");
    },

    async remove(userId: string): Promise<void> {
      // DELETE /org-users/{id} → permissions `DELETE /v0/users/{id}` (org taken
      // from the auth token). unwrapData throws a typed ApiError on `ok:false`
      // (e.g. 404 not a member, 400 self-removal) so the caller can react.
      unwrapData(
        await client.delete<unknown>(`/org-users/${encodeURIComponent(userId)}`),
      );
      client.invalidate("/org-users");
    },

    async resend(userId: string): Promise<{ emailSent?: boolean }> {
      // POST /org-users/{id}/resend → permissions `POST /v0/users/{id}/resend`
      // (org taken from the auth token). unwrapData throws a typed ApiError on
      // `ok:false` (e.g. 400 already accepted, 404 not a member).
      const result = unwrapData(
        await client.post<unknown>(
          `/org-users/${encodeURIComponent(userId)}/resend`,
          {},
        ),
      ) as { email_sent?: unknown } | null;
      return typeof result?.email_sent === "boolean" ? { emailSent: result.email_sent } : {};
    },
  };
}
